/**
 * Telegram WebView bootstrap — the ONLY Telegram-specific surface glue.
 *
 * We use the raw `window.Telegram.WebApp` object (injected by the platform SDK
 * loaded in index.html) rather than an npm SDK: the mini-app only ever runs
 * inside Telegram's WebView, and "forward the raw initData verbatim" (the auth
 * contract, ADR-082) needs nothing more than the string. A minimal typed facade
 * is declared here so the app code stays type-safe without a dependency.
 *
 * initData is deliberately NOT parsed or validated client-side — it's an opaque
 * blob handed to the gateway (see auth/telegramInitDataAuth.ts). We only read
 * `initDataUnsafe.user` for a cosmetic display name; it is never trusted for
 * auth (the name "…Unsafe" is Telegram's own — unsigned client-side).
 */

import type { SystemSchemeDetector } from '@agnivo/app-core';

export interface TelegramWebAppUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
}

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

export interface TelegramWebApp {
  /** Raw signed launch payload — forwarded verbatim to the gateway. */
  initData: string;
  /** Client-parsed, UNSIGNED convenience view — cosmetic use only. Optional:
   *  a bridge shipping no launch payload (desktop-Telegram gap) may omit it. */
  initDataUnsafe?: { user?: TelegramWebAppUser };
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  /** Signal the client the WebView chrome can be shown. */
  ready: () => void;
  /** Expand to full height (default is a collapsed sheet). */
  expand: () => void;
  /** Close the Mini App. Used by the honest session-recovery UX: closing lets the
   *  user reopen from Telegram, which delivers FRESH initData — the only recovery
   *  from a spent single-use launch payload. */
  close: () => void;
  /** Open a t.me link NATIVELY (switch to the chat inside Telegram) — the only
   *  guaranteed path; a plain <a> may open a browser in some clients. Optional:
   *  guard for very old clients that predate the API. */
  openTelegramLink?: (url: string) => void;
  onEvent: (event: 'themeChanged', handler: () => void) => void;
  offEvent: (event: 'themeChanged', handler: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/** Present when the page is genuinely running inside a Telegram WebView. */
export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * FALLBACK initData source: Telegram launches the webview with the signed
 * payload in the URL fragment (`#tgWebAppData=<urlencoded raw initData>&…`);
 * the telegram.org bridge script normally parses it into WebApp.initData. If
 * the bridge yields nothing (script failed to load / a client that injects
 * the fragment but a partial bridge — the 2026-07-18 desktop-Telegram class),
 * the payload may still be RIGHT THERE in the hash. Reading it ourselves is
 * security-neutral: the gateway validates the signature server-side; the
 * transport the blob took into the page doesn't matter.
 *
 * Read-only (never mutates the hash — the memory-router contract: the app
 * neither routes on nor rewrites the URL). Returns '' when absent.
 */
export function readInitDataFromHash(): string {
  // location optional-chained: unit-test window stubs (and any bridge-less
  // embedder) may not carry one — absence is just "no payload here".
  const hash = window.location?.hash ?? '';
  if (hash.length < 2) return '';
  try {
    return new URLSearchParams(hash.slice(1)).get('tgWebAppData') ?? '';
  } catch {
    return '';
  }
}

/** Which transport supplied initData this launch. `none` = no payload anywhere. */
export type InitDataSource = 'sdk' | 'hash' | 'none';

/**
 * The attribute the browser smoke reads. Exported so a spec pins the NAME
 * rather than a string literal that a rename would silently orphan — the
 * failure mode being a selector that matches nothing and a test that passes
 * because it counted zero.
 */
export const INIT_DATA_SOURCE_ATTR = 'data-init-data-source';

/**
 * Which transport won, given what each one yielded.
 *
 * A function rather than an expression at the call site so the TEST can execute
 * THIS code instead of a copy of it. An earlier draft inlined the ternary in
 * `doBootstrap` and re-declared it in the spec; that spec would have stayed
 * green through any change to the real one, which is the failure mode where a
 * test certifies its own duplicate.
 *
 * SDK wins ties. Telegram normally supplies the payload BOTH ways (fragment
 * present, bridge parses it), so both-present is the healthy launch — reporting
 * `hash` there would make every good launch look like a fallback.
 */
export function resolveInitDataSource(
  sdkInitData: string | undefined,
  hashInitData: string,
): InitDataSource {
  if (sdkInitData) return 'sdk';
  return hashInitData ? 'hash' : 'none';
}

/**
 * Publish the transport onto the document root. See the call site in
 * `auth/telegramInitDataAuth.ts` for why it is published at all and why here.
 *
 * Guarded on `document`: this module's other readers are called from node-env
 * unit suites that stub only `window`, and this must be the same no-op there
 * that they are — a throw here would fail the auth bootstrap itself, turning a
 * test-observability helper into a production dependency on the DOM.
 */
export function setInitDataSource(source: InitDataSource): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(INIT_DATA_SOURCE_ATTR, source);
}

/**
 * Map Telegram's theme params onto the design-system CSS variables. Called on
 * launch and re-called on `themeChanged`. Only maps params Telegram actually
 * sends; unset ones leave the @agnivo/ui defaults in place.
 *
 * The light/dark DECISION is NOT made here — it belongs to the app-core theme
 * system, which sets `data-theme` from the user's pref ('system' follows
 * `colorScheme` via telegramSystemSchemeDetector below; 'light'/'dark' force it).
 */
function applyThemeParams(webApp: TelegramWebApp): void {
  const root = document.documentElement;
  const p = webApp.themeParams;
  const map: Record<string, string | undefined> = {
    '--tg-bg-color': p.bg_color,
    '--tg-text-color': p.text_color,
    '--tg-hint-color': p.hint_color,
    '--tg-link-color': p.link_color,
    '--tg-button-color': p.button_color,
    '--tg-button-text-color': p.button_text_color,
    '--tg-secondary-bg-color': p.secondary_bg_color,
  };
  for (const [name, value] of Object.entries(map)) {
    if (value) root.style.setProperty(name, value);
  }
}

/**
 * How long the LIFECYCLE readers below wait for a late `async` SDK.
 *
 * Deliberately NOT the auth path's 200ms ceiling (architect ruling
 * 2026-08-14), and this is not a widening of it — it is a different concern
 * with a different failure mode. The auth path can afford a tight bound
 * because `readInitDataFromHash()` carries the auth-critical initData when the
 * poll loses. `ready()`/`expand()`/theme-params have NO fallback: miss them and
 * the mini-app stays un-expanded, unthemed, and never completes Telegram's
 * lifecycle handshake for the whole session. So this bound is sized to "the
 * SDK is coming, just slowly" rather than to first-paint latency.
 *
 * Still bounded, not infinite: outside Telegram (`pnpm dev` standalone, or the
 * desktop/web shells) the SDK never arrives, and these readers must settle into
 * the same no-op they have today rather than polling forever.
 */
export const SDK_LIFECYCLE_WAIT_MS = 3_000;

/**
 * The mini-app's SystemSchemeDetector (app-core theme system): resolves "system"
 * to Telegram's reported colorScheme and re-notifies on `themeChanged`. Defaults
 * to 'light' when running standalone (no Telegram host).
 */
export const telegramSystemSchemeDetector: SystemSchemeDetector = {
  getScheme: () => getTelegramWebApp()?.colorScheme ?? 'light',
  subscribe: (onChange) => {
    const immediate = getTelegramWebApp();
    if (immediate) {
      immediate.onEvent('themeChanged', onChange);
      return () => immediate.offEvent('themeChanged', onChange);
    }
    // `async` SDK: absent NOW is not absent FOREVER. Before the tag was async
    // this branch could only mean "standalone, no Telegram host" and returning
    // a no-op unsubscribe was correct. It no longer is — returning early here
    // would leave the theme pinned to getScheme()'s 'light' default for the
    // entire session on any launch where the SDK loses the race.
    let unsubscribed = false;
    let detach = (): void => {};
    void waitForTelegramWebApp(SDK_LIFECYCLE_WAIT_MS).then((arrived) => {
      if (unsubscribed || !arrived) return;
      const webApp = getTelegramWebApp();
      if (!webApp) return;
      webApp.onEvent('themeChanged', onChange);
      detach = () => webApp.offEvent('themeChanged', onChange);
      // The scheme was resolved against the 'light' default while the SDK was
      // still in flight, so re-notify: this call is what actually repaints a
      // dark-themed client that launched on a slow SDK.
      onChange();
    });
    // Unsubscribing BEFORE the SDK lands must still cancel the pending attach,
    // or a torn-down consumer gets wired to `themeChanged` after unmount.
    return () => {
      unsubscribed = true;
      detach();
    };
  },
};

/**
 * Bootstrap the WebView: signal ready, expand to full height, and wire theme
 * sync. Safe to call outside Telegram (returns without effect) so `pnpm dev`
 * works standalone against mock auth.
 */
export async function bootstrapTelegram(): Promise<void> {
  // Was a synchronous `if (!webApp) return;`. Under the `async` SDK tag that
  // early return became a live production bug rather than a standalone-dev
  // no-op: main.tsx calls this before first paint, so on any launch where the
  // SDK loses the race we would skip ready() + expand() + theme params for the
  // whole session — an un-expanded, unthemed mini-app that never completed
  // Telegram's handshake. Awaiting the bounded poll makes late arrival work and
  // keeps the standalone no-op intact (the poll simply times out).
  const arrived = await waitForTelegramWebApp(SDK_LIFECYCLE_WAIT_MS);
  if (!arrived) return; // standalone dev / non-Telegram host — no SDK, ever
  const webApp = getTelegramWebApp();
  if (!webApp) return;
  webApp.ready();
  webApp.expand();
  applyThemeParams(webApp);
  webApp.onEvent('themeChanged', () => applyThemeParams(webApp));
}

/**
 * Wait for the Telegram WebView bridge to inject `window.Telegram.WebApp`.
 *
 * The bridge is loaded via `<script async src="telegram.org/js/telegram-web-app.js">`
 * in `index.html`. Because that tag is `async`, it and the app module race by
 * design, so any reader may run BEFORE the SDK exists — that is the supported
 * state this helper is here to absorb.
 *
 * CITATION CORRECTED 2026-08-14: this docstring previously cited
 * `quality-tests #2757985481` as proof of "useEffect fired before the SDK
 * loaded". That attribution was WRONG. In that pipeline the tag was still
 * parser-blocking, so the deferred app module never executed at all — React
 * never mounted, and no reader in this file ever ran (the trace showed 5
 * requests, every one a static asset referenced by the HTML, and zero
 * app-originated traffic in 25s). The real fix for THAT failure was `async`
 * on the tag. The race this helper handles is the one `async` deliberately
 * introduces, and it is genuine — it is simply not what #2757985481 shows.
 *
 * The 200ms ceiling covers the typical <100ms SDK load on Telegram's CDN with
 * 2× headroom for transient slow loads; the URL-fragment fallback
 * (`readInitDataFromHash`) is the long-tail path — `doBootstrap()` reaches it
 * automatically on `false` because the awaited boolean gates the bridge read.
 *
 * Does NOT throw — the caller's `doBootstrap()` is structured to fall through
 * to the URL-fragment source on `false`. The poll runs at ~one frame (16ms)
 * granularity; this is cheap (no allocations, no DOM access) and lives next
 * to `getTelegramWebApp` + `readInitDataFromHash` in this file because the
 * auth provider should not own SDK-load-timing discipline inline.
 */
export function waitForTelegramWebApp(timeoutMs: number = 200): Promise<boolean> {
  // performance.now() (not Date.now()) so this helper works under vitest's
  // `useFakeTimers({ toFake: ['Date'] })` regime in the unit tests — the tests
  // freeze Date but leave performance alone, which is the same wall-clock
  // source the SDK's own load event uses.
  const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const now = (): number =>
    typeof performance !== 'undefined' ? performance.now() : Date.now();
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && getTelegramWebApp()) {
      resolve(true);
      return;
    }
    const tick = (): void => {
      // SSR / vitest tears the global down between tests — keep the poll alive
      // (resolve false) instead of throwing on the next `window` reference.
      if (typeof window === 'undefined') {
        resolve(false);
        return;
      }
      if (getTelegramWebApp()) {
        resolve(true);
        return;
      }
      if (now() - start >= timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(tick, 16);
    };
    setTimeout(tick, 16);
  });
}
